import { Button } from '@/components/ui/button'
import React, { useContext, useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { SettingsContext } from '@/context/settings-context'
import iconPlaceholder from '@/assets/images/media-buying/icon-placeholder.png'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import {
    ARCHIVE_LEAD_FORM,
    GET_PAGE_DESCRIPTION_DATA,
    UNARCHIVE_LEAD_FORM,
    CREATE_LEAD_FORM,
} from '@/graphql/media-buying-queries.js'
import { showNotification } from '@/utils/notifications'

import LeadFormTable from '../leadForms/index'
import { useLeadForms } from '../hooks/useLeadForms'
import { resolveFormType } from '../leadFormType'
import { LeadFormQuestionsView } from './instantForm/LeadFormQuestionsView'
import { CubeLoader } from '@/components/ui/cube-loader'

const Navigation = ({ goBack, handleNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button className="tw:w-[156px] tw:!text-sm" onClick={handleNext}>
                Next
            </Button>
        </div>
    )
}

const LeadFormOptions = ({ newLeadForm, onSelectExisting, onCreateNew }) => {
    return (
        <div>
            <RadioGroup
                value={newLeadForm ? 'new' : 'existing'}
                onValueChange={(value) => (value === 'existing' ? onSelectExisting() : onCreateNew())}
            >
                <div className="tw:flex tw:items-center tw:space-x-2 tw:gap-2">
                    <RadioGroupItem value="existing" id="existing" />
                    <Label htmlFor="existing" className="tw:cursor-pointer tw:text-sm tw:font-semibold">
                        Select from existing forms
                    </Label>
                </div>
                <div className="tw:flex tw:items-center tw:space-x-2 tw:gap-2">
                    <RadioGroupItem value="new" id="new" />
                    <Label htmlFor="new" className="tw:cursor-pointer tw:text-sm tw:font-semibold">
                        Create New Form
                    </Label>
                </div>
            </RadioGroup>
        </div>
    )
}

export const LeadForm = React.memo(() => {
    const {
        goBack,
        goNext,
        viewModel,
        getStepContentData,
        setStepContentData,
        setChildrenVisibility,
        registerCustomNavigation,
        clearCustomNavigation,
        clearStepAndChildren,
        dataForStepper,
    } = useWizard()
    const children = viewModel['leadForm']?.children || []
    const { connectedPublishers } = useMediaBuyingContext()
    const thisPublisher = Object.values(connectedPublishers || {})?.[0]
    const { publisher_id, page_id, campaign_id } = thisPublisher || {}

    const [newLeadForm, setShowNewLeadForm] = useState(false)
    const { forms, loading, fetchLeadForms, activeTab, setActiveTab } = useLeadForms(publisher_id, page_id, campaign_id)
    const [selectedForm, setSelectedForm] = useState({})

    const { theme } = useContext(SettingsContext)

    const [pageTitle, setPageTitle] = useState('Sunny Travels')
    const [pageIcon, setPageIcon] = useState(iconPlaceholder)

    useEffect(() => {
        document.body.classList.remove('light', 'dark')
        document.body.classList.add(theme)
    }, [theme])

    const [fetchPageDescriptionData, { data: pageDescriptionData }] = useLazyQuery(GET_PAGE_DESCRIPTION_DATA, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            err.graphQLErrors.forEach(({ message, extensions }) => {
                if (extensions.code === 'PUBLISHER_API_ERROR') {
                    showNotification(message, 'error', 3000, null, null, true, true)
                }
            })
        },
    })
    const [archiveLeadForm, { loading: archiving }] = useMutation(ARCHIVE_LEAD_FORM)
    const [unarchiveLeadForm, { loading: unArchiving }] = useMutation(UNARCHIVE_LEAD_FORM)
    const [createLeadForm, { loading: creatingInstantForm }] = useMutation(CREATE_LEAD_FORM)

    const handleArchive = async (formId) => {
        try {
            const result = await archiveLeadForm({
                variables: {
                    formId: formId,
                    publisherId: thisPublisher?.publisher_id,
                    pageId: thisPublisher?.page_id,
                    campaignId: thisPublisher?.campaign_id,
                },
            })
            if (result) {
                await fetchLeadForms()
            }
        } catch (err) {
            showNotification('Failed to archive lead form. Please try again.', 'error', 3000, null, null, true, true)
        }
    }

    const handleUnarchive = async (formId) => {
        try {
            const result = await unarchiveLeadForm({
                variables: {
                    formId: formId,
                    publisherId: thisPublisher?.publisher_id,
                    pageId: thisPublisher?.page_id,
                    campaignId: thisPublisher?.campaign_id,
                },
            })

            if (result) await fetchLeadForms()
        } catch (err) {
            showNotification('Failed to unarchive lead form. Please try again.', 'error', 3000, null, null, true, true)
        }
    }

    useEffect(() => {
        if (pageDescriptionData) {
            const { name, picture } = pageDescriptionData.publisherPageData || {}
            setPageIcon(picture?.data?.url || iconPlaceholder)
            setPageTitle(name || 'Sunny Travels')
        }
    }, [pageDescriptionData])

    useEffect(() => {
        if (connectedPublishers) {
            // fetch page description data
            const getPageData = async () => {
                await Promise.all([
                    await fetchLeadForms(),
                    await fetchPageDescriptionData({
                        variables: {
                            publisherId: thisPublisher?.publisher_id,
                            pageId: thisPublisher?.page_id,
                            campaignId: thisPublisher?.campaign_id,
                        },
                    }),
                ])
            }

            getPageData()
        }
    }, [connectedPublishers])

    const handleNext = async () => {
        if (!newLeadForm && !selectedForm) {
            showNotification(
                'Please Select a Form or click Create New lead Form to Proceed.',
                'error',
                3000,
                null,
                null,
                true,
                true,
            )

            return false
        }

        goNext()
    }

    const buildFormIntroState = (form) => {
        const contextCard = form?.context_card
        const hasGreeting = Boolean(contextCard?.title || contextCard?.content?.length)
        const isList = contextCard?.style === 'LIST_STYLE'

        return {
            hasBackgroundImage: false,
            backgroundImage: null,
            hasGreeting,
            greeting: {},
            headline: contextCard?.title || '',
            description: hasGreeting
                ? isList
                    ? {
                          descriptionType: 'list',
                          list: Array.isArray(contextCard?.content) ? contextCard.content : [],
                          paragraph: '',
                      }
                    : {
                          descriptionType: 'paragraph',
                          list: [],
                          paragraph: Array.isArray(contextCard?.content)
                              ? contextCard.content.join(' ')
                              : contextCard?.content || '',
                      }
                : {
                      descriptionType: 'paragraph',
                      list: [],
                      paragraph: '',
                  },
        }
    }

    const buildFormQuestionsState = (form) => {
        const safeQuestions = Array.isArray(form?.questions) ? form.questions : []

        return {
            questions: safeQuestions.map((question) => {
                const mappedQuestion = {
                    ...question,
                    optional: Boolean(question?.optional),
                }

                if (question?.type === 'CUSTOM') {
                    mappedQuestion.question = question?.label || question?.question || ''
                    mappedQuestion.options = Array.isArray(question?.options)
                        ? question.options.map((option) => option?.value).filter(Boolean)
                        : []
                }

                return mappedQuestion
            }),
            contactDescription: '',
        }
    }

    const handleEditForm = (form) => {
        if (!form || form.status !== 'ACTIVE') return

        const thankYouPage = form?.thank_you_page || {}
        const legalContent = form?.legal_content || {}
        const privacyPolicy = legalContent?.privacy_policy || {}
        const websiteUrl = thankYouPage?.website_url || form?.follow_up_action_url || ''

        clearStepAndChildren('leadForm')
        setChildrenVisibility('leadForm', true)

        setStepContentData(
            {
                formName: form?.name || '',
                formType: resolveFormType(form),
            },
            'formType',
        )
        setStepContentData(buildFormIntroState(form), 'formIntro')
        setStepContentData(buildFormQuestionsState(form), 'formQuestions')
        setStepContentData(
            {
                privacyPolicyUrl: form?.privacy_policy_url || privacyPolicy?.url || '',
                linkText: privacyPolicy?.link_text || '',
                customNotices: '',
                showCustomDisclaimer: false,
                customDisclaimer: '',
                disclaimerText: '',
                consents: [],
            },
            'formPrivacy',
        )
        setStepContentData(
            {
                thankYouMessage: thankYouPage?.title || "Thanks, you're all set.",
                description: thankYouPage?.body || 'You can visit our website or exit from the form now.',
                websiteButtonText: thankYouPage?.button_text || 'View website',
                websiteUrl,
            },
            'formThankYou',
        )

        setSelectedForm(form)
        setShowNewLeadForm(true)
        setStepContentData(
            {
                selectedForm: form,
                showNewLeadForm: true,
                editingFormId: form.id,
                formId: form.id,
                formName: form.name,
                formStatus: form.status,
                contextCard: form.context_card,
                thankYouPage: form.thank_you_page,
                privacyPolicyUrl: form.privacy_policy_url,
                followUpActionUrl: form.follow_up_action_url,
                legalContent: form.legal_content,
                locale: form.locale,
                isOptimizedForQuality: form.is_optimized_for_quality,
                questionPageCustomHeadline: form.question_page_custom_headline,
                followUpActionText: form.follow_up_action_text,
                meta_form_id: form.id,
            },
            'leadForm',
        )
    }

    useEffect(() => {
        // set page data storage
        const cachedData = getStepContentData()
        if (cachedData) {
            setShowNewLeadForm(cachedData.showNewLeadForm || false)
            setSelectedForm(cachedData.selectedForm || null)
            // All other form data is already stored in cachedData and will be available via getStepContentData()
        } else {
            setStepContentData({
                showNewLeadForm: viewModel['leadForm']?.children[Object.keys(children)[0]]?.visible,
                selectedForm: null,
            })
        }
    }, [])

    const onExistingFormSelect = () => {
        setChildrenVisibility('leadForm', false)
        setStepContentData({ showNewLeadForm: false, editingFormId: null })
        setShowNewLeadForm(false)
    }

    const onCreateFormSelect = () => {
        clearStepAndChildren('leadForm')
        setChildrenVisibility('leadForm', true)
        setStepContentData({
            showNewLeadForm: true,
            selectedForm: null,
            editingFormId: null,
        })
        setShowNewLeadForm(true)
        setSelectedForm(null)
        // we need to reset the entire flow.

        setChildrenVisibility('leadForm', true)
    }

    useEffect(() => {
        registerCustomNavigation(<Navigation goBack={goBack} handleNext={handleNext} />)

        return () => clearCustomNavigation()
    }, [newLeadForm, selectedForm, thisPublisher, pageDescriptionData])

    const showLoader = archiving || unArchiving || creatingInstantForm

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="leadForm" />
            <div className={`d-flex h-100 ${theme} campaigns-container w-100`}>
                <div className="flex-grow-1 border-right-white-1 overflow-auto pt-4">
                    <div className="tw:px-6 tw:pb-6">
                        <LeadFormOptions
                            newLeadForm={newLeadForm}
                            onSelectExisting={onExistingFormSelect}
                            onCreateNew={onCreateFormSelect}
                        />
                    </div>

                    <div className="flex w-full max-w-sm flex-col gap-6">
                        <Tabs
                            defaultValue={activeTab}
                            className="tw:pl-6"
                            onValueChange={(value) => setActiveTab(value)}
                        >
                            <TabsList className="tw:p-0">
                                <TabsTrigger value="active">Active</TabsTrigger>
                                <TabsTrigger value="archived">Archived</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    <div className="position-relative w-100" style={{ height: '380px' }}>
                        {showLoader && (
                            <div className="tw:w-full tw:flex tw:items-center tw:justify-center tw:h-full">
                                <CubeLoader size="default" fullScreen={false} className="tw:items-center tw:h-full" />
                            </div>
                        )}

                        {!showLoader && (
                            <LeadFormTable
                                forms={forms}
                                readonly={activeTab === 'archived'}
                                loading={loading}
                                externalSelectedForm={selectedForm || {}}
                                onRowClick={(form) => {
                                    setSelectedForm(form)
                                    setShowNewLeadForm(false)
                                    // Store all form data in contentCache for use by other components
                                    setStepContentData({
                                        selectedForm: form,
                                        showNewLeadForm: false,
                                        editingFormId: null,
                                        // Extract all form data for step cache
                                        formId: form.id,
                                        formName: form.name,
                                        formStatus: form.status,
                                        contextCard: form.context_card,
                                        thankYouPage: form.thank_you_page,
                                        privacyPolicyUrl: form.privacy_policy_url,
                                        followUpActionUrl: form.follow_up_action_url,
                                        legalContent: form.legal_content,
                                        locale: form.locale,
                                        isOptimizedForQuality: form.is_optimized_for_quality,
                                        questionPageCustomHeadline: form.question_page_custom_headline,
                                        followUpActionText: form.follow_up_action_text,
                                        meta_form_id: form.id, // Store form ID for later use
                                    })
                                    setChildrenVisibility('leadForm', false)
                                }}
                                onDuplicate={() => {}}
                                onArchive={async (form) => {
                                    await handleArchive(form.id)

                                    if (selectedForm?.id === form.id) {
                                        setSelectedForm(null)
                                        setShowNewLeadForm(true)
                                        setStepContentData({
                                            selectedForm: null,
                                            showNewLeadForm: true,
                                            editingFormId: null,
                                        })
                                        clearStepAndChildren('leadForm')
                                        setChildrenVisibility('leadForm', true)
                                    }
                                }}
                                onUnarchive={async (form) => {
                                    await handleUnarchive(form.id)
                                }}
                                onRefresh={async (ev) => {
                                    await fetchLeadForms()
                                }}
                                onEdit={handleEditForm}
                            />
                        )}
                    </div>
                </div>

                {/*Mock Phone section for form*/}
                <div className="position-relative tw:p-6" style={{ width: 320, maxWidth: 320 }}>
                    <LeadFormQuestionsView
                        questions={selectedForm?.questions || []}
                        pageIcon={pageIcon}
                        pageTitle={pageTitle}
                        theme={theme}
                        // Pass all form data from cache
                        contextCard={getStepContentData()?.contextCard}
                        thankYouPage={getStepContentData()?.thankYouPage}
                        privacyPolicyUrl={getStepContentData()?.privacyPolicyUrl}
                        followUpActionUrl={getStepContentData()?.followUpActionUrl}
                        legalContent={getStepContentData()?.legalContent}
                        questionPageCustomHeadline={getStepContentData()?.questionPageCustomHeadline}
                        followUpActionText={getStepContentData()?.followUpActionText}
                    />
                </div>
            </div>
        </div>
    )
})
